import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSettingsStore } from '../../stores/settingsStore';
import BackgroundSelector from './BackgroundSelector';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface SettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const SettingsPanel: React.FC<SettingsPanelProps> = ({ isOpen, onClose }) => {
  const settings = useSettingsStore();
  const { updateSettings } = settings;

  const fontFamilies = [
    { value: 'serif', label: 'Serif (Crimson Text)', className: 'font-serif' },
    { value: 'sans', label: 'Sans (Inter)', className: 'font-sans' },
    { value: 'mono', label: 'Mono (JetBrains)', className: 'font-mono' },
  ];

  const themes = [
    { value: 'light', label: '☀️ Light' },
    { value: 'dark', label: '🌙 Dark' },
  ];

  const backgroundStyles = [
    { value: 'solid', label: '🎨 Solid Color' },
    { value: 'gradient', label: '🌈 Gradient' },
    { value: 'image', label: '🖼️ Image' },
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-40"
            onClick={onClose}
          />
          <motion.div
            initial={{ x: 400, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 400, opacity: 0 }}
            transition={{ type: 'spring', damping: 25 }}
            className="fixed right-0 top-0 h-full w-96 bg-white dark:bg-gray-900 shadow-2xl z-50 overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-bold">Settings</h2>
                <button
                  onClick={onClose}
                  className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Theme */}
                <div>
                  <label className="block text-sm font-medium mb-2">Theme</label>
                  <div className="grid grid-cols-2 gap-2">
                    {themes.map((theme) => (
                      <button
                        key={theme.value}
                        onClick={() => updateSettings({ theme: theme.value as any })}
                        className={`p-3 rounded-lg border-2 transition-all ${
                          settings.theme === theme.value
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                            : 'border-gray-300 dark:border-gray-600'
                        }`}
                      >
                        {theme.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Font Family */}
                <div>
                  <label className="block text-sm font-medium mb-2">Font Family</label>
                  <div className="space-y-2">
                    {fontFamilies.map((font) => (
                      <button
                        key={font.value}
                        onClick={() => updateSettings({ fontFamily: font.value as any })}
                        className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                          settings.fontFamily === font.value
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                            : 'border-gray-300 dark:border-gray-600'
                        }`}
                      >
                        <span className={font.className}>{font.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Font Size */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Font Size: {settings.fontSize}px
                  </label>
                  <input
                    type="range"
                    min="8"
                    max="36"
                    value={settings.fontSize}
                    onChange={(e) => updateSettings({ fontSize: parseInt(e.target.value) })}
                    className="w-full accent-blue-500"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>Very Small</span>
                    <span>Extra Large</span>
                  </div>
                </div>

                {/* Text Color */}
                <div>
                  <label className="block text-sm font-medium mb-2">Text Color</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => updateSettings({ textColor: 'white' })}
                      className={`p-3 rounded-lg border-2 transition-all flex items-center justify-center space-x-2 ${
                        settings.textColor === 'white'
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-300 dark:border-gray-600'
                      }`}
                    >
                      <div className="w-4 h-4 rounded-full bg-white border border-gray-300"></div>
                      <span>White</span>
                    </button>
                    <button
                      onClick={() => updateSettings({ textColor: 'black' })}
                      className={`p-3 rounded-lg border-2 transition-all flex items-center justify-center space-x-2 ${
                        settings.textColor === 'black'
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-300 dark:border-gray-600'
                      }`}
                    >
                      <div className="w-4 h-4 rounded-full bg-black"></div>
                      <span>Black</span>
                    </button>
                  </div>
                </div>

                {/* Line Height */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Line Height: {settings.lineHeight}
                  </label>
                  <input
                    type="range"
                    min="1.2"
                    max="2.5"
                    step="0.1"
                    value={settings.lineHeight}
                    onChange={(e) => updateSettings({ lineHeight: parseFloat(e.target.value) })}
                    className="w-full accent-blue-500"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>Compact</span>
                    <span>Spacious</span>
                  </div>
                </div>

                {/* Max Width */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Editor Width: {settings.maxWidth}px
                  </label>
                  <input
                    type="range"
                    min="600"
                    max="1200"
                    step="50"
                    value={settings.maxWidth}
                    onChange={(e) => updateSettings({ maxWidth: parseInt(e.target.value) })}
                    className="w-full accent-blue-500"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>Narrow</span>
                    <span>Wide</span>
                  </div>
                </div>

                {/* Editor Height */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Editor Height: {settings.editorHeight}vh
                  </label>
                  <input
                    type="range"
                    min="50"
                    max="95"
                    step="5"
                    value={settings.editorHeight}
                    onChange={(e) => updateSettings({ editorHeight: parseInt(e.target.value) })}
                    className="w-full accent-blue-500"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>Short</span>
                    <span>Full Height</span>
                  </div>
                </div>

                {/* Background Style */}
                <div>
                  <label className="block text-sm font-medium mb-2">Background Style</label>
                  <div className="space-y-2">
                    {backgroundStyles.map((style) => (
                      <button
                        key={style.value}
                        onClick={() => updateSettings({ backgroundStyle: style.value as any })}
                        className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                          settings.backgroundStyle === style.value
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                            : 'border-gray-300 dark:border-gray-600'
                        }`}
                      >
                        {style.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Background Selector */}
                <div>
                  <label className="block text-sm font-medium mb-2">Background Options</label>
                  <BackgroundSelector />
                </div>

                {/* 글래스모피즘 설정 */}
                <div className="space-y-4 border-t pt-4">
                  <h3 className="text-lg font-semibold">✨ Editor Glass Effect</h3>
                  
                  {/* 글래스모피즘 토글 */}
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={settings.useGlassmorphism}
                      onChange={(e) => updateSettings({ useGlassmorphism: e.target.checked })}
                      className="w-5 h-5 rounded"
                    />
                    <span className="text-sm font-medium">Enable Glass Effect</span>
                  </label>

                  {settings.useGlassmorphism && (
                    <>
                      {/* 투명도 조절 */}
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Transparency: {Math.round(settings.editorOpacity * 100)}%
                        </label>
                        <input
                          type="range"
                          min="0"
                          max="30"
                          step="1"
                          value={settings.editorOpacity * 100}
                          onChange={(e) => updateSettings({ editorOpacity: parseInt(e.target.value) / 100 })}
                          className="w-full accent-blue-500"
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>Clear</span>
                          <span>Opaque</span>
                        </div>
                      </div>

                      {/* 블러 강도 조절 */}
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Blur Intensity: {settings.editorBlur}px
                        </label>
                        <input
                          type="range"
                          min="0"
                          max="50"
                          step="5"
                          value={settings.editorBlur}
                          onChange={(e) => updateSettings({ editorBlur: parseInt(e.target.value) })}
                          className="w-full accent-blue-500"
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>Sharp</span>
                          <span>Heavy Blur</span>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Auto Save */}
                <div>
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={settings.autoSave}
                      onChange={(e) => updateSettings({ autoSave: e.target.checked })}
                      className="w-5 h-5 rounded"
                    />
                    <span className="text-sm font-medium">Auto Save</span>
                  </label>
                </div>

                {/* Show Word Count */}
                <div>
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={settings.showWordCount}
                      onChange={(e) => updateSettings({ showWordCount: e.target.checked })}
                      className="w-5 h-5 rounded"
                    />
                    <span className="text-sm font-medium">Show Word Count</span>
                  </label>
                </div>

                {/* Show Toolbar */}
                <div>
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={settings.showToolbar}
                      onChange={(e) => updateSettings({ showToolbar: e.target.checked })}
                      className="w-5 h-5 rounded"
                    />
                    <span className="text-sm font-medium">Show Toolbar</span>
                  </label>
                </div>

                {/* Word Goal */}
                <div>
                  <label className="flex items-center space-x-3 mb-2">
                    <input
                      type="checkbox"
                      checked={settings.showWordGoal}
                      onChange={(e) => updateSettings({ showWordGoal: e.target.checked })}
                      className="w-5 h-5 rounded"
                    />
                    <span className="text-sm font-medium">Show Word Goal</span>
                  </label>
                  {settings.showWordGoal && (
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Daily Word Goal: {settings.wordGoal} words
                      </label>
                      <input
                        type="range"
                        min="100"
                        max="5000"
                        step="50"
                        value={settings.wordGoal}
                        onChange={(e) => updateSettings({ wordGoal: parseInt(e.target.value) })}
                        className="w-full"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SettingsPanel;